import { Mail, ArrowRight, CheckCircle2, ShieldCheck, Info, Sparkles } from 'lucide-react';
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../lib/firebase';

enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
    tenantId?: string | null;
    providerInfo?: {
      providerId?: string | null;
      email?: string | null;
    }[];
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
      tenantId: auth.currentUser?.tenantId,
      providerInfo: auth.currentUser?.providerData?.map(provider => ({
        providerId: provider.providerId,
        email: provider.email,
      })) || []
    },
    operationType,
    path
  }
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  throw new Error(JSON.stringify(errInfo));
}

export default function Newsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const validateEmail = (email: string) => {
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      return "Please enter a valid email address with characters and a dot (.) after the @ sign.";
    }
    
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const error = validateEmail(email.trim());
    if (error) {
      setStatus('error');
      setErrorMsg(error);
      return;
    }

    setStatus('loading');
    setErrorMsg('');

    try {
      const targetEmail = email.trim().toLowerCase();
      const path = 'tla_subscribers';
      const data = {
        email: targetEmail,
        createdAt: serverTimestamp()
      };
      console.log("Attempting newsletter subscription:", path, data);
      await addDoc(collection(db, path), data);
      
      // Dispatch automated welcome newsletter confirmation email via server
      try {
        await fetch("/api/send-newsletter", {
          method: "POST",
          headers: {
            "Content-Type": "application/json"
          },
          body: JSON.stringify({ email: targetEmail })
        });
      } catch (emailErr) {
        console.error("Non-blocking error dispatching newsletter confirmation email:", emailErr);
      }

      setStatus('success');
      setEmail('');
    } catch (err) {
      setStatus('error');
      setErrorMsg('Something went wrong. Please try again later.');
      handleFirestoreError(err, OperationType.CREATE, 'tla_subscribers');
    }
  };

  return (
    <section className="py-24 border-t border-agora-ink/5 w-full relative">
      {/* Success Modal */}
      <AnimatePresence>
        {status === 'success' && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-6 bg-agora-ink/80 backdrop-blur-md"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              className="bg-white rounded-[3.5rem] p-10 md:p-16 max-w-2xl w-full text-center space-y-8 shadow-2xl relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-64 h-64 bg-agora-tan rounded-full blur-[100px] opacity-20 -translate-y-1/2 translate-x-1/2" />
              
              <div className="w-28 h-28 bg-agora-olive/10 text-agora-olive rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle2 size={56} strokeWidth={1.5} />
              </div>
              
              <div className="space-y-6">
                <h3 className="text-4xl md:text-5xl font-serif font-bold tracking-tight text-agora-ink">Welcome to the inner circle!</h3>
                <p className="text-agora-ink/60 text-lg md:text-xl leading-relaxed font-sans max-w-lg mx-auto">
                  Your journey into Windsor Essex's finest local experiences has officially begun.
                </p>
                <div className="py-10 px-8 bg-agora-tan/15 rounded-[2.5rem] border border-agora-tan/30 mt-8 relative">
                  <p className="font-serif italic text-2xl text-agora-ink leading-relaxed">
                    "When we support local masters, we don't just buy a product; we keep a story alive. Thank you for your support."
                  </p>
                </div>
              </div>

              <button 
                onClick={() => setStatus('idle')}
                className="w-full h-20 bg-agora-ink text-white rounded-[1.5rem] text-xl font-bold hover:bg-agora-rust transition-all shadow-xl active:scale-[0.98]"
              >
                Continue Exploring
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div 
        whileHover={{ y: -5 }}
        transition={{ type: "spring", stiffness: 300 }}
        className="bg-agora-ink text-agora-cream rounded-[4rem] px-8 py-16 md:p-20 relative overflow-hidden shadow-2xl border border-white/5"
      >
        {/* Abstract Background Accents */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-agora-rust rounded-full blur-[120px] opacity-20 -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-agora-tan rounded-full blur-[100px] opacity-10 translate-y-1/2 -translate-x-1/2" />
        
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent pointer-events-none" />
        <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-6 space-y-10">
            <div className="inline-flex items-center gap-3 px-4 py-2 bg-white/10 rounded-full border border-white/10 text-agora-tan text-xs font-bold tracking-[0.2em] uppercase">
              <Sparkles size={14} className="text-agora-rust" /> 
              Curated Community Newsletter
            </div>
            
            <div className="space-y-6">
              <h3 className="text-5xl md:text-7xl font-serif font-bold tracking-tight leading-[0.9]">
                Stay in the <br /> <span className="text-agora-rust">Loop.</span>
              </h3>
              <p className="text-agora-cream/50 leading-relaxed font-sans text-xl max-w-xl">
                The heartbeat of our city, delivered with care. Discover stories, events, and masters you won't find anywhere else.
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 pt-4">
              <div className="flex -space-x-3">
                {[
                  {
                    src: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=120&h=120",
                    alt: "Active local member - Young woman"
                  },
                  {
                    src: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=120&h=120",
                    alt: "Local supporter - Young man"
                  },
                  {
                    src: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=120&h=120",
                    alt: "Community regular - Professional woman"
                  },
                  {
                    src: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&q=80&w=120&h=120",
                    alt: "Local artisan patron - Mature man"
                  }
                ].map((avatar, i) => (
                  <div key={i} className="w-12 h-12 rounded-full border-2 border-agora-ink bg-agora-tan/20 flex items-center justify-center overflow-hidden">
                    <img 
                      src={avatar.src} 
                      alt={avatar.alt} 
                      loading="lazy"
                      width={48}
                      height={48}
                      referrerPolicy="no-referrer"
                    />
                  </div>
                ))}
                <div className="w-12 h-12 rounded-full border-2 border-agora-ink bg-agora-rust flex items-center justify-center text-[10px] font-black">
                  180+
                </div>
              </div>
              <p className="text-sm font-medium text-agora-cream/40">
                Join 180+ locals supporting <br className="hidden sm:block" /> Windsor Essex's finest masters.
              </p>
            </div>
          </div>

          <div className="lg:col-span-6 w-full max-w-xl mx-auto lg:ml-auto">
            <div className="bg-white/[0.03] backdrop-blur-xl p-6 md:p-10 rounded-[3.5rem] border border-white/10 shadow-[0_0_50px_-12px_rgba(0,0,0,0.5)]">
              <form className="space-y-8" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  <label htmlFor="newsletter-email" className="text-[10px] uppercase tracking-[0.3em] font-black text-agora-rust px-2">Your Local Connection</label>
                  <div className="space-y-4">
                    <div className="relative">
                      <input 
                        id="newsletter-email"
                        type="email" 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="your@email.com" 
                        aria-label="Email address for newsletter"
                        disabled={status === 'loading'}
                        className="w-full h-20 bg-white/5 border-2 border-white/10 rounded-[1.8rem] px-8 outline-none focus:ring-4 ring-agora-rust/10 focus:border-agora-rust/40 focus:bg-white/10 transition-all font-sans text-xl text-white placeholder:text-white/20 disabled:opacity-50 shadow-inner"
                      />
                      <Mail className="absolute right-6 top-1/2 -translate-y-1/2 opacity-20" size={24} />
                    </div>
                    
                    <button 
                      type="submit"
                      disabled={status === 'loading'}
                      className="w-full h-20 bg-agora-rust text-white rounded-[1.8rem] text-xl font-bold flex items-center justify-center gap-3 hover:bg-white hover:text-agora-ink hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 group shadow-[0_20px_50px_-20px_rgba(196,87,54,0.5)] disabled:opacity-50"
                    >
                      {status === 'loading' ? (
                        <div className="flex items-center gap-3">
                          <motion.div 
                            animate={{ rotate: 360 }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                            className="w-6 h-6 border-3 border-white/20 border-t-white rounded-full"
                          />
                          <span>Syncing...</span>
                        </div>
                      ) : (
                        <>
                          Join the Marketplace <ArrowRight size={22} className="group-hover:translate-x-1 transition-transform" />
                        </>
                      )}
                    </button>
                  </div>
                </div>
                
                {status === 'error' && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-4 text-agora-rust text-sm font-bold bg-agora-rust/10 p-5 rounded-2xl border border-agora-rust/20"
                  >
                    <Info size={18} className="shrink-0 mt-0.5" />
                    <span>{errorMsg}</span>
                  </motion.div>
                )}

                <div className="pt-4 border-t border-white/5 grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-agora-olive/20 flex items-center justify-center text-agora-olive">
                      <ShieldCheck size={16} />
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-bold opacity-30">Zero Spam</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-agora-olive/20 flex items-center justify-center text-agora-olive">
                      <CheckCircle2 size={16} />
                    </div>
                    <span className="text-[10px] uppercase tracking-widest font-bold opacity-30">Secure Data</span>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </motion.div>
    </section>
  );
}

